import dayjs from 'dayjs'
import { useGeckoPermissions } from 'features/permissions'
import { TimeRange } from 'features/systemLens'
import {
  BuildUiProvider,
  useBuildUiView,
  WindowDrawRole,
} from 'features/timeSeries/experiment/BuildUiContext'
import {
  buildExperimentBandOverlay,
  buildOutOfBandPoints,
  buildRemovedPointOverlays,
  ExperimentBandOverlay,
  experimentBandScaleExtent,
  ExperimentOutOfBandPoint,
  ExperimentRemovedPoints,
  experimentTestBoundaryMs,
} from 'features/timeSeries/experiment/overlays'
import { reconcileConfigTagsWithPens } from 'features/timeSeries/experiment/penSync'
import {
  defaultExperimentConfig,
  ExperimentConfig,
  ExperimentRun,
} from 'features/timeSeries/experiment/types'
import { useExperimentApi } from 'features/timeSeries/experiment/useExperimentApi'
import { TimeSeriesService } from 'features/timeSeries/timeSeriesService'
import { CustomAxisRangeMap } from 'features/timeSeries/utils/axisRangeHelpers'
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { BaseSensorEntry, PERMISSION_GECKO_INTERNAL_MEMBER } from 'types'

/**
 * Session state of the in-viewer Experiment Lab (the EXPERIMENT sidebar tab).
 *
 * Lives in a provider mounted by `ScatterplotProvider` — NOT in the tab
 * component — so switching sidebar tabs or closing the sidebar never throws
 * away a configured experiment or a finished training run. The provider also
 * owns the chart-facing side effects: target/driver tags sync bidirectionally
 * with the viewer's pens and the experiment date window is pushed to the
 * viewer's time range, so the main chart (with zoom/tooltip/ruler) is the
 * experiment's time-series surface.
 */
interface ExperimentLabContextValue {
  config: ExperimentConfig
  setConfig: Dispatch<SetStateAction<ExperimentConfig>>
  patchConfig: (partial: Partial<ExperimentConfig>) => void
  api: ReturnType<typeof useExperimentApi>
  runs: ExperimentRun[]
  setRuns: Dispatch<SetStateAction<ExperimentRun[]>>
  /** Method whose expected/residual band draws on the chart + diagnostics. */
  selectedMethodKey: string
  setSelectedMethodKey: Dispatch<SetStateAction<string>>
  /** Master toggle for the experiment overlays on the main chart. */
  showOverlays: boolean
  setShowOverlays: Dispatch<SetStateAction<boolean>>
  /** Hide every pen except the target (pen visibility only — config intact). */
  targetOnlyView: boolean
  setTargetOnlyView: Dispatch<SetStateAction<boolean>>
  /** Save/load state that must survive tab remounts to keep "Update save". */
  saveName: string
  setSaveName: Dispatch<SetStateAction<string>>
  savedEntryId: string | null
  setSavedEntryId: Dispatch<SetStateAction<string | null>>
  lagsText: string
  setLagsText: Dispatch<SetStateAction<string>>
  hiddenText: string
  setHiddenText: Dispatch<SetStateAction<string>>
  /** Copy the viewer's visible range into the experiment date window. */
  useViewerRange: () => void
  /**
   * Explicitly re-push the experiment's configured date window to the
   * viewer's time range — the way back after zooming/panning away. Same
   * gates and path as the automatic date push.
   */
  resetViewerToExperimentWindow: () => void
  /** Chart-facing overlay data (null/empty when hidden or not available). */
  bandOverlay: ExperimentBandOverlay | null
  removedPoints: ExperimentRemovedPoints[]
  /**
   * Marks the experiment session as engaged (the tab was opened). Passive
   * chart sessions never call the experiment API: the pens↔tags sync runs
   * regardless (it's local state), but the debounced preview fetch waits for
   * engagement.
   */
  markEngaged: () => void
  engaged: boolean
  /** Buckets where the actual left expected ± band, time-ascending. */
  outOfBandPoints: ExperimentOutOfBandPoint[]
  /** Entry pool for tag-metadata lookups (TagLabel name/description). */
  sensorEntryPool: BaseSensorEntry[]
  /** Epoch-ms start of the test split, for the chart demarcation line. */
  testBoundaryMs: number | null
  /** y-extent the target scale must reach for the band to fit on-plot. */
  bandScaleExtent: { min: number; max: number } | null
}

const ExperimentLabContext = createContext<ExperimentLabContextValue | null>(
  null
)

export interface ExperimentLabProviderProps {
  children: ReactNode
  siteID: string
  /** Pen-resolution pool from ScatterplotProvider (same one addSentinel uses). */
  sensorEntryPool: BaseSensorEntry[]
  /** The viewer's pens — read for chart→experiment sync and interval calc. */
  selectedSensors: BaseSensorEntry[]
  setSelectedSensors: Dispatch<SetStateAction<BaseSensorEntry[]>>
  /** Per-pen visibility (absent = visible) — the target-only view toggles it. */
  setSensorVisibilityConfig: Dispatch<SetStateAction<Record<string, boolean>>>
  /**
   * The viewer's per-pen custom Y bounds (the same map the Pens table's
   * Y Min/Y Max columns edit). The experiment's `config.y_ranges` seeds
   * into it so pinned experiment bounds render on the chart.
   */
  setCustomAxisRanges: Dispatch<SetStateAction<CustomAxisRangeMap>>
  /**
   * Reports the band's y-extent per target pen UP to ScatterplotProvider,
   * which widens that pen's auto scale range so the band fits on-plot. Must
   * be identity-stable and equality-guarded by the parent.
   */
  reportScaleExtents: (
    extents: Record<string, { min: number; max: number }>
  ) => void
  timeRange: TimeRange
  setTimeRange: Dispatch<SetStateAction<TimeRange>>
  exitLiveTail: () => void
}

export const ExperimentLabProvider = ({
  children,
  siteID,
  sensorEntryPool,
  selectedSensors,
  setSelectedSensors,
  setSensorVisibilityConfig,
  setCustomAxisRanges,
  reportScaleExtents,
  timeRange,
  setTimeRange,
  exitLiveTail,
}: ExperimentLabProviderProps) => {
  // Seed the experiment window from the viewer's current range (rather than
  // the standalone lab's 2-year default) so picking a target doesn't yank the
  // viewer to a huge window.
  const [config, setConfig] = useState<ExperimentConfig>(() => ({
    ...defaultExperimentConfig(),
    start_date: dayjs(timeRange.from).format('YYYY-MM-DD'),
    end_date: dayjs(timeRange.to).format('YYYY-MM-DD'),
  }))
  const [runs, setRuns] = useState<ExperimentRun[]>([])
  const [selectedMethodKey, setSelectedMethodKey] = useState('')
  const [showOverlays, setShowOverlays] = useState(true)
  const [targetOnlyView, setTargetOnlyView] = useState(false)
  const [saveName, setSaveName] = useState('')
  const [savedEntryId, setSavedEntryId] = useState<string | null>(null)
  const [lagsText, setLagsText] = useState('1,3,6')
  const [hiddenText, setHiddenText] = useState('64,32')
  const api = useExperimentApi(siteID || null)

  /**
   * The Experiment Lab is gecko-internal, like Sentinels. The tab button and
   * its content are gated in ScatterplotSidebar, but this provider is always
   * mounted under ScatterplotProvider — so every chart-facing side effect it
   * owns (pen/tag sync, date sync, the debounced preview fetch, pen
   * visibility, overlay building) is ALSO gated here. A non-internal user
   * gets zero experiment code paths and zero fim-service experiment calls,
   * mirroring how useSentinelScores gates scoring at its choke point.
   */
  const { hasPermissions } = useGeckoPermissions()
  const experimentEnabled = hasPermissions([PERMISSION_GECKO_INTERNAL_MEMBER])

  // Pens now import into the config automatically (see the sync invariant
  // below), so a target+driver config exists on any multi-pen chart. The
  // preview fetch must not fire for users who never opened the Experiment
  // tab — `engaged` flips true on first tab mount and stays true for the
  // session.
  const [engaged, setEngaged] = useState(false)
  const markEngaged = useCallback(() => setEngaged(true), [])

  const patchConfig = useCallback((partial: Partial<ExperimentConfig>) => {
    setConfig((c) => ({ ...c, ...partial }))
  }, [])

  const driversKey = config.drivers.join('|')
  const cleaningPlanKey = JSON.stringify(config.cleaning_plan)
  const phaseShiftsKey = JSON.stringify(config.phase_shifts)
  const featureKey = JSON.stringify(config.feature)
  const splitKey = JSON.stringify(config.split)

  // Debounced data preview (stale-while-revalidate via the hook — the old
  // preview stays visible while refetching). Fires only once a target and
  // drivers exist, so idle charts never call the experiment API.
  const { fetchPreview } = api
  useEffect(() => {
    if (
      !experimentEnabled ||
      !engaged ||
      !config.target ||
      !config.drivers.length
    ) {
      return
    }
    const t = window.setTimeout(() => {
      void fetchPreview(config)
    }, 350)
    return () => window.clearTimeout(t)
    // Intentionally depend on the config snapshot fields the preview cares
    // about, not the whole object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config.target,
    driversKey,
    config.start_date,
    config.end_date,
    config.sampling_mode,
    config.interval_minutes,
    config.interval,
    config.interval_unit,
    config.aggregation_op,
    config.gap_fill,
    config.gate_scope,
    cleaningPlanKey,
    phaseShiftsKey,
    config.corr_shift_max,
    config.corr_shift_step,
    featureKey,
    splitKey,
    config.fit_cap,
    fetchPreview,
    experimentEnabled,
    engaged,
  ])

  // Keep the selected method valid against the latest train result.
  useEffect(() => {
    const keys = Object.keys(api.trainResult?.methods ?? {})
    if (!keys.length) return
    setSelectedMethodKey((prev) => (keys.includes(prev) ? prev : keys[0]))
  }, [api.trainResult])

  /**
   * The server files every finished run itself, so a run is already saved by
   * the time it reaches us. Adopt that entry as this session's save target so
   * pressing Save renames the autosave in place instead of leaving the same
   * run behind under two names.
   *
   * Only when nothing is claimed yet: a session that loaded a saved
   * experiment keeps saving back to it, and its autosaves age out of the
   * rolling window on their own.
   */
  useEffect(() => {
    const autosavedId = api.trainResult?.autosaved_entry_id
    if (!autosavedId) return
    setSavedEntryId((prev) => prev ?? autosavedId)
  }, [api.trainResult])

  /**
   * Bidirectional pen ↔ experiment-tag sync.
   *
   * THE INVARIANT (documented in full on reconcileConfigTagsWithPens): the
   * viewer's pens and the experiment's tag set are the SAME SET. Target =
   * first tag to arrive, never auto-overwritten while its pen is present;
   * every other pen is a driver; removals apply on both sides.
   *
   * Two convergent effects, each a no-op once the sides match (that shared
   * fixed point is what prevents feedback loops — no ownership bookkeeping):
   *
   * - experiment → pens (diff-based): a tag added in the tab gains a pen
   *   (the same way `addSentinel` adds its target pen); a tag removed in the
   *   tab drops its pen, whoever originally added it.
   * - pens → experiment (reconciliation-based): the config's tag set is
   *   reconciled to equal the CURRENT pen list on every pen change,
   *   including the mount pass — pens already on the chart import, a saved
   *   chart's restored pens import, and a user add can never be swallowed by
   *   event-ordering. (The previous diff-based version kept a pen baseline
   *   and re-baselined on multi-pen changes, which silently dropped
   *   legitimate adds — the "pen doesn't show up in the experiment" bug.)
   */
  const tagsKey = [config.target, ...config.drivers].filter(Boolean).join('|')

  // experiment → pens
  const prevTagsRef = useRef<string[]>([])
  useEffect(() => {
    if (!experimentEnabled) return
    const tags = tagsKey ? tagsKey.split('|') : []
    const tagSet = new Set(tags)
    const removedTags = prevTagsRef.current.filter((tag) => !tagSet.has(tag))
    prevTagsRef.current = tags

    setSelectedSensors((prev) => {
      let next = prev
      if (removedTags.length) {
        const filtered = next.filter(
          (sensor) => !removedTags.includes(sensor.id)
        )
        if (filtered.length !== next.length) next = filtered
      }
      const missingEntries = tags
        .filter((tag) => !next.some((sensor) => sensor.id === tag))
        .map((tag) => sensorEntryPool.find((sensor) => sensor.id === tag))
        .filter((entry): entry is BaseSensorEntry => entry !== undefined)
      if (missingEntries.length) next = [...next, ...missingEntries]
      return next
    })
  }, [tagsKey, sensorEntryPool, setSelectedSensors, experimentEnabled])

  // pens → experiment
  const penIdsKey = selectedSensors.map((sensor) => sensor.id).join('|')
  useEffect(() => {
    if (!experimentEnabled) return
    const penIds = penIdsKey ? penIdsKey.split('|') : []
    setConfig((c) => {
      const next = reconcileConfigTagsWithPens(c.target, c.drivers, penIds)
      return next ? { ...c, ...next } : c
    })
  }, [penIdsKey, experimentEnabled])

  /**
   * Date sync: the experiment window drives the viewer's time range, so the
   * pens/overlays on the chart always show the span the experiment trains
   * over. One-way by design — viewer zooms must NOT rewrite the experiment
   * window (that would silently change what gets trained); "Use chart range"
   * is the explicit path back.
   */
  const lastPushedWindowRef = useRef(`${config.start_date}..${config.end_date}`)
  const penCountRef = useRef(selectedSensors.length)
  useEffect(() => {
    penCountRef.current = selectedSensors.length
  }, [selectedSensors.length])

  /**
   * The one push path: exits live tail, recomputes the interval for the
   * window/pen count, and moves the viewer. Records the pushed window so
   * the automatic date-sync effect doesn't re-fire for the same dates.
   */
  const pushExperimentWindowToViewer = useCallback(
    (startDate: string, endDate: string) => {
      const from = dayjs(startDate)
      // end_date is an inclusive UTC day in the experiment API.
      const to = dayjs(endDate).endOf('day')
      if (!from.isValid() || !to.isValid() || !to.isAfter(from)) return
      lastPushedWindowRef.current = `${startDate}..${endDate}`

      const { interval, interval_unit } =
        TimeSeriesService.getIntervalForPenCount(
          to.diff(from, 'seconds'),
          Math.max(1, penCountRef.current)
        )
      exitLiveTail()
      setTimeRange({
        from: from.toISOString(),
        to: to.toISOString(),
        preset: 'custom',
        interval,
        intervalUnit: interval_unit,
      })
    },
    [exitLiveTail, setTimeRange]
  )

  useEffect(() => {
    // Only steer the viewer once an experiment is actually being set up.
    if (!experimentEnabled || !engaged || !config.target) return
    const windowKey = `${config.start_date}..${config.end_date}`
    if (lastPushedWindowRef.current === windowKey) return
    pushExperimentWindowToViewer(config.start_date, config.end_date)
  }, [
    config.target,
    config.start_date,
    config.end_date,
    pushExperimentWindowToViewer,
    experimentEnabled,
    engaged,
  ])

  /**
   * Explicit-click reset: re-pushes the CURRENT config dates even when they
   * match the last pushed window (the user zoomed away and wants back).
   * Same internal-permission + engaged gates as the automatic push, so it
   * can never steer a chart whose experiment session was never opened.
   */
  const resetViewerToExperimentWindow = useCallback(() => {
    if (!experimentEnabled || !engaged) return
    pushExperimentWindowToViewer(config.start_date, config.end_date)
  }, [
    experimentEnabled,
    engaged,
    config.start_date,
    config.end_date,
    pushExperimentWindowToViewer,
  ])

  const useViewerRange = useCallback(() => {
    const start = dayjs(timeRange.from).format('YYYY-MM-DD')
    const end = dayjs(timeRange.to).format('YYYY-MM-DD')
    // The viewer already shows this window; don't push it back.
    lastPushedWindowRef.current = `${start}..${end}`
    setConfig((c) => ({ ...c, start_date: start, end_date: end }))
  }, [timeRange.from, timeRange.to])

  /**
   * Target-only view: hides every non-target pen through the viewer's
   * per-pen visibility config, NOT by removing pens — so the pen ↔
   * experiment-tag sync (which watches the pen LIST) never sees it and the
   * experiment config is untouched. Only pens THIS toggle hid are restored
   * on the way out, tracked in a ref, so a pen the user hid via the pen
   * drawer before toggling stays hidden after (with one caveat: a pen hidden
   * BOTH ways comes back visible when the toggle clears).
   */
  const targetOnlyHiddenRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    const desiredHidden = targetOnlyView
      ? (penIdsKey ? penIdsKey.split('|') : []).filter(
          (id) => id !== config.target
        )
      : []
    const desired = new Set(desiredHidden)
    const ours = targetOnlyHiddenRef.current
    const toHide = desiredHidden.filter((id) => !ours.has(id))
    const toShow = [...ours].filter((id) => !desired.has(id))
    if (!toHide.length && !toShow.length) return
    setSensorVisibilityConfig((prev) => {
      const next = { ...prev }
      for (const id of toHide) next[id] = false
      for (const id of toShow) next[id] = true
      return next
    })
    for (const id of toHide) ours.add(id)
    for (const id of toShow) ours.delete(id)
  }, [targetOnlyView, penIdsKey, config.target, setSensorVisibilityConfig])

  /**
   * Seed/sync the viewer's custom Y bounds from the experiment's
   * `config.y_ranges` — the same customAxisRanges pathway the Pens table's
   * Y Min/Y Max columns edit (resolveYAxisRange: custom wins over auto).
   * Bounds the experiment previously applied but no longer carries are
   * removed so a cleared row reverts that pen to auto range; bounds set
   * directly in the Pens table for tags the experiment never touched are
   * left alone. Deliberately NOT the band auto-widen extent path
   * (reportScaleExtents), which stays band-only.
   */
  const yRangesKey = JSON.stringify(config.y_ranges || {})
  const appliedYRangesRef = useRef<CustomAxisRangeMap>({})
  useEffect(() => {
    if (!experimentEnabled || !engaged) return
    const next = config.y_ranges || {}
    const prevApplied = appliedYRangesRef.current
    appliedYRangesRef.current = next
    setCustomAxisRanges((prev) => {
      let changed = false
      const merged = { ...prev }
      for (const id of Object.keys(prevApplied)) {
        const kept = next[id]?.min !== undefined || next[id]?.max !== undefined
        if (!kept && id in merged) {
          delete merged[id]
          changed = true
        }
      }
      for (const [id, range] of Object.entries(next)) {
        if (range.min === undefined && range.max === undefined) continue
        const current = merged[id]
        if (
          !current ||
          current.min !== range.min ||
          current.max !== range.max
        ) {
          merged[id] = { min: range.min, max: range.max }
          changed = true
        }
      }
      return changed ? merged : prev
    })
    // The y_ranges snapshot is keyed by content (yRangesKey), not identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yRangesKey, experimentEnabled, engaged, setCustomAxisRanges])

  const bandOverlay = useMemo(
    () =>
      experimentEnabled && showOverlays
        ? buildExperimentBandOverlay(api.trainResult, selectedMethodKey)
        : null,
    [experimentEnabled, showOverlays, api.trainResult, selectedMethodKey]
  )

  const removedPoints = useMemo(
    () =>
      experimentEnabled && showOverlays
        ? buildRemovedPointOverlays(api.preview)
        : [],
    [experimentEnabled, showOverlays, api.preview]
  )

  const outOfBandPoints = useMemo(
    () => buildOutOfBandPoints(bandOverlay),
    [bandOverlay]
  )

  const testBoundaryMs = useMemo(
    () =>
      experimentEnabled && showOverlays
        ? experimentTestBoundaryMs(api.trainResult, selectedMethodKey)
        : null,
    [experimentEnabled, showOverlays, api.trainResult, selectedMethodKey]
  )

  const bandScaleExtent = useMemo(
    () => (bandOverlay ? experimentBandScaleExtent(bandOverlay) : null),
    [bandOverlay]
  )

  // Push the band's y-extent up to the scatterplot so the target pen's auto
  // scale widens to fit the band (a pinned pen-data-only range clips it).
  useEffect(() => {
    reportScaleExtents(
      bandOverlay && bandScaleExtent
        ? { [bandOverlay.targetSensorId]: bandScaleExtent }
        : {}
    )
  }, [bandOverlay, bandScaleExtent, reportScaleExtents])

  const value = useMemo(
    () => ({
      config,
      setConfig,
      patchConfig,
      api,
      runs,
      setRuns,
      selectedMethodKey,
      setSelectedMethodKey,
      showOverlays,
      setShowOverlays,
      targetOnlyView,
      setTargetOnlyView,
      saveName,
      setSaveName,
      savedEntryId,
      setSavedEntryId,
      lagsText,
      setLagsText,
      hiddenText,
      setHiddenText,
      useViewerRange,
      resetViewerToExperimentWindow,
      markEngaged,
      engaged,
      bandOverlay,
      removedPoints,
      outOfBandPoints,
      sensorEntryPool,
      testBoundaryMs,
      bandScaleExtent,
    }),
    [
      config,
      patchConfig,
      api,
      runs,
      selectedMethodKey,
      showOverlays,
      targetOnlyView,
      saveName,
      savedEntryId,
      lagsText,
      hiddenText,
      useViewerRange,
      resetViewerToExperimentWindow,
      markEngaged,
      engaged,
      bandOverlay,
      removedPoints,
      outOfBandPoints,
      sensorEntryPool,
      testBoundaryMs,
      bandScaleExtent,
    ]
  )

  return (
    <ExperimentLabContext.Provider value={value}>
      <BuildUiProvider>{children}</BuildUiProvider>
    </ExperimentLabContext.Provider>
  )
}

export const useExperimentLab = (): ExperimentLabContextValue => {
  const context = useContext(ExperimentLabContext)
  if (!context) {
    throw new Error(
      'useExperimentLab must be used within an ExperimentLabProvider'
    )
  }
  return context
}

const EMPTY_REMOVED_POINTS: ExperimentRemovedPoints[] = []
const EMPTY_OUT_OF_BAND: ExperimentOutOfBandPoint[] = []
const EMPTY_TAG_POOL: BaseSensorEntry[] = []

/**
 * Tag-metadata pool for `TagLabel` tooltips. Null-safe: surfaces rendered
 * without an ExperimentLabProvider simply find no metadata and fall back to
 * the bare tag id.
 */
export const useExperimentTagPool = (): BaseSensorEntry[] => {
  const context = useContext(ExperimentLabContext)
  return context?.sensorEntryPool ?? EMPTY_TAG_POOL
}

interface WindowsToolView {
  windowDrawRole: WindowDrawRole
  setWindowDrawRole: (role: WindowDrawRole) => void
  config: ExperimentConfig | null
  patchConfig: ((partial: Partial<ExperimentConfig>) => void) | null
  showSplitWindows: boolean
  engaged: boolean
}

/**
 * Chart-facing view of the split-windows tool. Null-safe: surfaces without
 * an ExperimentLabProvider have no armed role and draw no windows.
 */
export const useWindowsToolView = (): WindowsToolView => {
  const context = useContext(ExperimentLabContext)
  const buildUi = useBuildUiView()
  return {
    windowDrawRole: buildUi.windowDrawRole,
    setWindowDrawRole: buildUi.setWindowDrawRole,
    config: context?.config ?? null,
    patchConfig: context?.patchConfig ?? null,
    showSplitWindows: buildUi.showSplitWindows,
    engaged: context?.engaged ?? false,
  }
}

/**
 * Chart-facing view of the experiment overlays. Null-safe: chart surfaces
 * rendered without an ExperimentLabProvider (tests, embedded charts) simply
 * draw no experiment overlays.
 */
export const useExperimentChartOverlays = (): {
  bandOverlay: ExperimentBandOverlay | null
  removedPoints: ExperimentRemovedPoints[]
  outOfBandPoints: ExperimentOutOfBandPoint[]
  testBoundaryMs: number | null
  showTargetBounds: boolean
  showOutOfBoundsFlags: boolean
  showResidual: boolean
} => {
  const context = useContext(ExperimentLabContext)
  const buildUi = useBuildUiView()
  return {
    bandOverlay: context?.bandOverlay ?? null,
    removedPoints: context?.removedPoints ?? EMPTY_REMOVED_POINTS,
    outOfBandPoints: context?.outOfBandPoints ?? EMPTY_OUT_OF_BAND,
    testBoundaryMs: context?.testBoundaryMs ?? null,
    showTargetBounds: buildUi.showTargetBounds,
    showOutOfBoundsFlags: buildUi.showOutOfBoundsFlags,
    showResidual: buildUi.showResidual,
  }
}
